/* =============================================================================
   api.js  —  THE SINGLE STORAGE MODULE (production swap of the prototype's
   data.jsx). Every read/write of guest + host data goes through the `DB` object
   below, now backed by fetch() calls to the Cloudflare Worker. The DB surface is
   byte-for-byte identical to the prototype so no component logic changes.

   - Admin routes send `Authorization: Bearer <token>` from sessionStorage.
   - Public writes (RSVP, wish, prediction) carry a Turnstile token, which the
     forms add to the record object before calling add*(). No signature change.
   - getWishes() routes to the admin list (with emails) when an admin token is
     present, otherwise to the public, email-stripped list. This lets the guest
     wall and the admin keepsakes view share one method, as the components expect.
============================================================================= */

const API_BASE = import.meta.env.VITE_API_BASE || '';
const TOKEN_KEY = 'llb-admin-token';

/* ------------------------------ admin token ------------------------------- */
export function getAdminToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}
export function setAdminToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* sessionStorage unavailable; token simply will not persist */
  }
}
export function clearAdminToken() {
  setAdminToken('');
}
export function hasAdminToken() {
  return !!getAdminToken();
}

/* ------------------------------- id helper -------------------------------- */
// Kept identical to the prototype so admin-generated ids (registry, faq,
// schedule, gallery, gift) match. The server accepts a client id when present.
export function makeId(prefix) {
  return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
}

/* ------------------------------ fetch wrapper ----------------------------- */
async function req(method, path, { body, admin } = {}) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (admin) {
    const token = getAdminToken();
    if (token) headers['Authorization'] = 'Bearer ' + token;
  }

  let res;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error('We could not reach the server. Please check your connection.');
  }

  if (!res.ok) {
    let message = `Request failed (${res.status}).`;
    try {
      const data = await res.json();
      if (data && (data.message || data.error)) message = data.message || data.error;
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

/* --------------------------------- DB API --------------------------------- */
export const DB = {
  /* settings */
  getSettings: () => req('GET', '/api/settings'),
  saveSettings: (settings) => req('PUT', '/api/settings', { body: settings, admin: true }),

  /* rsvps */
  getRSVPs: () => req('GET', '/api/rsvps', { admin: true }),
  addRSVP: (rsvp) => req('POST', '/api/rsvps', { body: rsvp }),
  removeRSVP: (id) => req('DELETE', `/api/rsvps/${encodeURIComponent(id)}`, { admin: true }),

  /* wishes (dual-routed by admin presence) */
  getWishes: () =>
    hasAdminToken()
      ? req('GET', '/api/wishes', { admin: true })
      : req('GET', '/api/wishes/public'),
  addWish: (wish) => req('POST', '/api/wishes', { body: wish }),
  removeWish: (id) => req('DELETE', `/api/wishes/${encodeURIComponent(id)}`, { admin: true }),

  /* predictions */
  getPredictions: () => req('GET', '/api/predictions', { admin: true }),
  addPrediction: (p) => req('POST', '/api/predictions', { body: p }),
  removePrediction: (id) => req('DELETE', `/api/predictions/${encodeURIComponent(id)}`, { admin: true }),

  /* gallery (whole-array replace) */
  getGalleryItems: () => req('GET', '/api/gallery'),
  saveGalleryItems: (items) => req('PUT', '/api/gallery', { body: items, admin: true }),

  /* gift log (whole-array replace, admin only) */
  getGiftLog: () => req('GET', '/api/giftlog', { admin: true }),
  saveGiftLog: (entries) => req('PUT', '/api/giftlog', { body: entries, admin: true }),

  /* email templates (admin) */
  getEmailTemplates: () => req('GET', '/api/email/templates', { admin: true }),
  saveEmailTemplates: (templates) => req('PUT', '/api/email/templates', { body: templates, admin: true }),

  /* mass-email broadcast (admin) */
  broadcastEmail: (payload) => req('POST', '/api/email/broadcast', { body: payload, admin: true }),
};
